import React, { useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';

/**
 * Modal component for displaying success, error, warning, or info messages
 * Supports dark/light theme and uses colors from SettingsContext
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {function} props.onClose - Callback when modal is closed
 * @param {string} props.type - Modal type: 'success' | 'error' | 'warning' | 'info'
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message content
 * @param {string} props.confirmText - Text for confirm button (default: 'OK')
 * @param {string} props.cancelText - Text for cancel button (optional)
 * @param {function} props.onConfirm - Callback when confirm button is clicked
 * @param {function} props.onCancel - Callback when cancel button is clicked
 * @param {boolean} props.showCancel - Whether to show cancel button
 * @param {React.ReactNode} props.children - Custom content to render inside modal
 */
const Modal = ({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = false,
  children,
}) => {
  // Theme context available for future use if needed
  useTheme();
  const { settings } = useSettings();

  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const successColor = settings.theme?.colors?.success || '#40C057';
  const errorColor = settings.theme?.colors?.danger || '#FA5252';
  const warningColor = settings.theme?.colors?.warning || '#FAB005';
  const infoColor = settings.theme?.colors?.info || '#339AF0';

  // Get colors based on modal type
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          iconBg: 'bg-green-100 dark:bg-green-900/30',
          iconColor: successColor,
          borderColor: 'border-green-200 dark:border-green-800',
        };
      case 'error':
        return {
          iconBg: 'bg-red-100 dark:bg-red-900/30',
          iconColor: errorColor,
          borderColor: 'border-red-200 dark:border-red-800',
        };
      case 'warning':
        return {
          iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
          iconColor: warningColor,
          borderColor: 'border-yellow-200 dark:border-yellow-800',
        };
      case 'info':
      default:
        return {
          iconBg: 'bg-blue-100 dark:bg-blue-900/30',
          iconColor: infoColor,
          borderColor: 'border-blue-200 dark:border-blue-800',
        };
    }
  };

  // Get icon based on type
  const getIcon = () => {
    const typeStyles = getTypeStyles();
    switch (type) {
      case 'success':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={typeStyles.iconColor}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={typeStyles.iconColor}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={typeStyles.iconColor}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={typeStyles.iconColor}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Handle escape key to close modal
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose?.();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  // Handle confirm button click
  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  // Handle cancel button click
  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  if (!isOpen) return null;

  const typeStyles = getTypeStyles();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70 backdrop-blur-sm transition-opacity duration-200"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`
          w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl
          transform transition-all duration-200 ease-out
          border ${typeStyles.borderColor}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center gap-4 p-6 pb-4">
          {/* Icon */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${typeStyles.iconBg} transition-colors duration-250`}>
            {getIcon()}
          </div>
          
          {/* Title */}
          <div className="flex-1 min-w-0">
            <h3
              id="modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-white transition-colors duration-250"
            >
              {title}
            </h3>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 pb-4">
          {message && (
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed transition-colors duration-250">
              {message}
            </p>
          )}
          {children}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl border-t border-gray-100 dark:border-gray-700 transition-colors duration-250">
          {showCancel && (
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-150"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-150"
            style={{ backgroundColor: type === 'error' ? errorColor : primaryColor }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Modal;
